import { X as XIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  candidateStatuses,
  candidateTags,
  createDefaultCandidateFilters,
  type CandidateFilters,
} from '@/types/candidate';

const formatDisplayDate = (date: Date | null) => {
  if (!date) {
    return '';
  }
  return date.toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

type ActiveFilterChipsProps = {
  filters: CandidateFilters;
  onFiltersChange: (filters: CandidateFilters) => void;
  onFiltersReset?: () => void;
  className?: string;
};

const ActiveFilterChips = ({
  filters,
  onFiltersChange,
  onFiltersReset,
  className,
}: ActiveFilterChipsProps) => {
  type Chip = {
    key: string;
    label: string;
    removeLabel: string;
    onRemove: () => void;
  };

  const chips: Chip[] = [];

  filters.tags.forEach((tagId) => {
    const tag = candidateTags.find((candidateTag) => candidateTag.id === tagId);
    const label = tag ? tag.label : tagId;

    chips.push({
      key: `tag-${tagId}`,
      label,
      removeLabel: `Verwijder ${label}`,
      onRemove: () =>
        onFiltersChange({
          ...filters,
          tags: filters.tags.filter((currentTagId) => currentTagId !== tagId),
        }),
    });
  });

  const statusFilter = filters.status
    ? candidateStatuses.find((status) => status.id === filters.status)
    : null;

  const { from, to } = filters.dateRange;
  const hasDateFilter = Boolean(from || to);
  let dateFilterLabel: string | null = null;

  if (hasDateFilter) {
    if (from && to) {
      dateFilterLabel = `${formatDisplayDate(from)} – ${formatDisplayDate(to)}`;
    } else if (from) {
      dateFilterLabel = `Vanaf ${formatDisplayDate(from)}`;
    } else if (to) {
      dateFilterLabel = `Tot ${formatDisplayDate(to)}`;
    }
  }

  if (statusFilter) {
    chips.push({
      key: 'status',
      label: statusFilter.label,
      removeLabel: `Verwijder status ${statusFilter.label}`,
      onRemove: () =>
        onFiltersChange({
          ...filters,
          status: null,
        }),
    });
  }

  if (dateFilterLabel) {
    chips.push({
      key: 'date-range',
      label: dateFilterLabel,
      removeLabel: 'Verwijder datumfilter',
      onRemove: () =>
        onFiltersChange({
          ...filters,
          dateRange: {
            from: null,
            to: null,
          },
        }),
    });
  }

  if (chips.length === 0) {
    return null;
  }

  const handleClearAllFilters = () => {
    if (onFiltersReset) {
      onFiltersReset();
      return;
    }

    onFiltersChange(createDefaultCandidateFilters());
  };

  return (
    <div className={cn('flex w-full flex-wrap items-center gap-2 sm:w-auto', className)}>
      <button
        type="button"
        onClick={handleClearAllFilters}
        className='w-7 h-7 flex items-center rounded-md bg-primary text-white transition hover:border-red-600/50 hover:bg-red-600/10 cursor-pointer'
      >
        <XIcon strokeWidth={3} className="h-3 w-3 mx-auto" />
        <span className="sr-only">Verwijder alle filters</span>
      </button>
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={chip.onRemove}
          className='h-7 flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium transition hover:border-primary/50 hover:bg-primary/15 cursor-pointer'
        >
          {chip.label}
          <XIcon className="h-3 w-3" />
          <span className="sr-only">{chip.removeLabel}</span>
        </button>
      ))}
    </div>
  );
};

export default ActiveFilterChips;
